/**
 * Signup OTP — calls CoreMonit Platform API (FastAPI).
 * See services/backendMode.ts for URL / proxy behavior.
 */

import { apiConnectionHint, getBackendBaseUrl, isBackendOtpEnabled } from '../backendMode';

export { isBackendOtpEnabled };

function extractErrorMessage(data: unknown, status: number): string {
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    const detail = d.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
      const inner = detail as Record<string, unknown>;
      if (typeof inner.message === 'string') return inner.message;
    }
    if (typeof d.message === 'string') return d.message;
  }
  return `Request failed (${status})`;
}

export async function requestSignupOtp(email: string, phoneNumber: string): Promise<void> {
  const base = getBackendBaseUrl();
  if (!isBackendOtpEnabled()) {
    throw new Error('Server OTP is disabled; check backendMode / env flags.');
  }

  let res: Response;
  try {
    res = await fetch(`${base}/api/auth/otp/signup/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, phone_number: phoneNumber }),
    });
  } catch (e) {
    if (e instanceof TypeError) {
      throw new Error(apiConnectionHint(base));
    }
    throw e;
  }

  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  if (!res.ok) {
    throw new Error(extractErrorMessage(data, res.status));
  }
  if (data.success === false) {
    throw new Error(typeof data.message === 'string' ? data.message : 'Failed to send verification code');
  }
}

export async function verifySignupOtp(email: string, otp: string): Promise<boolean> {
  const base = getBackendBaseUrl();
  if (!isBackendOtpEnabled()) {
    return false;
  }

  let res: Response;
  try {
    res = await fetch(`${base}/api/auth/otp/signup/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });
  } catch (e) {
    if (e instanceof TypeError) {
      throw new Error(apiConnectionHint(base));
    }
    throw e;
  }

  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  if (res.ok) {
    return data.success === true;
  }
  if (res.status === 400) {
    return false;
  }
  throw new Error(extractErrorMessage(data, res.status));
}
